import React from 'react';
import { Box, Typography, Stack, Switch, TextField } from '@mui/material';
import { ColorSwatchPicker } from '../../../business/ColorSwatchPicker/ColorSwatchPicker';

const BORDER_OPTIONS = [
  { val: 'default', label: 'ברירת מחדל' },
  { val: 'colored', label: 'זהב' },
  { val: 'none',    label: 'ללא מסגרת' },
];

/**
 * AppearanceTab — טאב עיצוב משותף לכל הרכיבים
 * Props: { formData, onChange, showTitleSection }
 */
const AppearanceTab = ({ formData, onChange, showTitleSection = true, componentKey }) => {
  return (
    <Stack spacing={3} dir="rtl">

      {/* כותרת */}
      {showTitleSection && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}>כותרת הרכיב</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>
                {formData.showTitle ? 'מופיע' : 'מוסתר'}
              </Typography>
              <Switch
                checked={formData.showTitle ?? true}
                onChange={(e) => onChange({ showTitle: e.target.checked })}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#111827' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#111827' },
                }}
              />
            </Stack>
          </Stack>
          <TextField
            fullWidth
            size="small"
            placeholder="כותרת הרכיב"
            value={formData.title || ''}
            onChange={(e) => onChange({ title: e.target.value })}
            disabled={!(formData.showTitle ?? true)}
            sx={{
              bgcolor: (formData.showTitle ?? true) ? 'white' : '#f9fafb',
              '& .MuiOutlinedInput-root': {
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
              },
            }}
          />
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.5 }}>
            הכותרת מוצגת בראש הרכיב — ניתן להסתירה לעיצוב נקי ללא כיתוב
          </Typography>
        </Box>
      )}

      {/* הגדרות תפילות חוזרות — TefilotWidget בלבד */}
      {componentKey === 'TefilotWidget' && (
        <Box>
          <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', mb: 1.5 }}>תפילות חוזרות</Typography>

          {/* recurringDisplay */}
          <Box mb={2}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>תצוגת תפילה חוזרת</Typography>
            <Stack direction="row" spacing={1}>
              {[
                { val: 'compact', label: 'טווח בלבד' },
                { val: 'full',    label: 'כל השעות'  },
              ].map(opt => (
                <Box
                  key={opt.val}
                  onClick={() => onChange({ recurringDisplay: opt.val })}
                  sx={{
                    flex: 1, py: 0.75, px: 1, borderRadius: 1.5, cursor: 'pointer',
                    textAlign: 'center', fontSize: '0.8rem', fontWeight: 500,
                    userSelect: 'none', transition: 'all 0.15s',
                    bgcolor: (formData.recurringDisplay || 'compact') === opt.val ? '#111827' : 'white',
                    color:   (formData.recurringDisplay || 'compact') === opt.val ? 'white' : '#6b7280',
                    border:  `1px solid ${(formData.recurringDisplay || 'compact') === opt.val ? '#111827' : '#e5e7eb'}`,
                    '&:hover': { borderColor: '#9ca3af' },
                  }}
                >
                  {opt.label}
                </Box>
              ))}
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.75 }}>
              "טווח בלבד" מציג "כל 15 דק׳ בין 13:00 ל-16:00" — "כל השעות" מציג את כל המופעים בנפרד
            </Typography>
          </Box>

          {/* showRuleMode */}
          <Box mb={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>הצג כלל במקום שעה</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.25 }}>
                  זמנים יחסיים יוצגו כנוסחה (כגון: 20 דק׳ לפני השקיעה)
                </Typography>
              </Box>
              <Switch
                checked={formData.showRuleMode ?? false}
                onChange={(e) => onChange({ showRuleMode: e.target.checked })}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#111827' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#111827' },
                }}
              />
            </Stack>
          </Box>

          {/* highlightNextMinyan */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>הדגש מניין קרוב</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.25 }}>
                  מסמן בצורה ויזואלית את המניין הבא הקרוב ביותר
                </Typography>
              </Box>
              <Switch
                checked={formData.highlightNextMinyan ?? false}
                onChange={(e) => onChange({ highlightNextMinyan: e.target.checked })}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#111827' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#111827' },
                }}
              />
            </Stack>
          </Box>
        </Box>
      )}

      {/* עיצוב */}
      <Box>
        <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', mb: 1.5 }}>עיצוב</Typography>

        {/* צבע רקע */}
        <Box mb={2}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>צבע רקע</Typography>
          <ColorSwatchPicker
            value={formData.bgColor || ''}
            onChange={(c) => onChange({ bgColor: c })}
            showDefaultOption
          />
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.75 }}>
            "ברירת מחדל" משתמש בצבע המובנה של התבנית
          </Typography>
        </Box>

        {/* סוג מסגרת */}
        <Box>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>סוג מסגרת</Typography>
          <Stack direction="row" spacing={1}>
            {BORDER_OPTIONS.map(opt => (
              <Box
                key={opt.val}
                onClick={() => onChange({ borderType: opt.val })}
                sx={{
                  flex: 1, py: 0.75, px: 1, borderRadius: 1.5, cursor: 'pointer',
                  textAlign: 'center', fontSize: '0.8rem', fontWeight: 500,
                  userSelect: 'none', transition: 'all 0.15s',
                  bgcolor: (formData.borderType || 'default') === opt.val ? '#111827' : 'white',
                  color:   (formData.borderType || 'default') === opt.val ? 'white' : '#6b7280',
                  border:  `1px solid ${(formData.borderType || 'default') === opt.val ? '#111827' : '#e5e7eb'}`,
                  '&:hover': { borderColor: '#9ca3af' },
                }}
              >
                {opt.label}
              </Box>
            ))}
          </Stack>
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.75 }}>
            המסגרת מקיפה את הרכיב — "זהב" מוסיף גוון קלאסי, "ברירת מחדל" מתאימה לרוב הסגנונות
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
};

export default AppearanceTab;
